/* eslint-disable max-classes-per-file */
import { type MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

export default abstract class MediaSelectionError extends Error {
  userFacingDescriptor?: MessageDescriptor;

  /** The class name for metric tags, since minification rewrites `constructor.name`. */
  errorType = 'MediaSelectionError';

  constructor(public message: string) {
    super(message);
    this.name = 'MediaSelectionError';
    // Set the prototype explicitly, see https://github.com/Microsoft/TypeScript-wiki/blob/main/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
    // extending classes will also need to do this
    Object.setPrototypeOf(this, MediaSelectionError.prototype);
  }
}

export class MediaSelectionNoFilesError extends MediaSelectionError {
  errorType = 'MediaSelectionNoFilesError';

  static message = 'No files selected';

  static userFacingMessage = msg`No files were selected, please try again`;

  constructor() {
    super(MediaSelectionNoFilesError.message);
    Object.setPrototypeOf(this, MediaSelectionNoFilesError.prototype);
    this.userFacingDescriptor = MediaSelectionNoFilesError.userFacingMessage;
  }
}

export class MediaSelectionUserCancelledError extends MediaSelectionError {
  errorType = 'MediaSelectionUserCancelledError';

  static message = 'Selection cancelled';

  static userFacingMessage = undefined;

  constructor() {
    super(MediaSelectionUserCancelledError.message);
    Object.setPrototypeOf(this, MediaSelectionUserCancelledError.prototype);
  }
}

export class MediaSelectionInvalidFileTypeError extends MediaSelectionError {
  errorType = 'MediaSelectionInvalidFileTypeError';

  static message = 'Invalid media file type';

  static userFacingMessage = msg`Invalid media type selected, please try again`;

  constructor() {
    super(MediaSelectionInvalidFileTypeError.message);
    Object.setPrototypeOf(this, MediaSelectionInvalidFileTypeError.prototype);
    this.userFacingDescriptor = MediaSelectionInvalidFileTypeError.userFacingMessage;
  }
}

export class MediaSelectionCorruptedFileError extends MediaSelectionError {
  errorType = 'MediaSelectionCorruptedFileError';

  static message = 'File contents do not match extension';

  static userFacingMessage = msg`File contents do not match file extension, please use a different file`;

  constructor() {
    super(MediaSelectionCorruptedFileError.message);
    Object.setPrototypeOf(this, MediaSelectionCorruptedFileError.prototype);
    this.userFacingDescriptor = MediaSelectionCorruptedFileError.userFacingMessage;
  }
}

export class MediaSelectionCameraUnavailableError extends MediaSelectionError {
  errorType = 'MediaSelectionCameraUnavailableError';

  static message = 'Camera is unavailable';

  static userFacingMessage = msg`Camera is unavailable, please try library media selection`;

  constructor() {
    super(MediaSelectionCameraUnavailableError.message);
    Object.setPrototypeOf(this, MediaSelectionCameraUnavailableError.prototype);
    this.userFacingDescriptor = MediaSelectionCameraUnavailableError.userFacingMessage;
  }
}

export class MediaSelectionPermissionsError extends MediaSelectionError {
  errorType = 'MediaSelectionPermissionsError';

  static message = 'Permissions not granted';

  static userFacingMessage = msg`Media permissions not granted, please allow access`;

  constructor() {
    super(MediaSelectionPermissionsError.message);
    Object.setPrototypeOf(this, MediaSelectionPermissionsError.prototype);
    this.userFacingDescriptor = MediaSelectionPermissionsError.userFacingMessage;
  }
}

export class MediaSelectionResizeError extends MediaSelectionError {
  errorType = 'MediaSelectionResizeError';

  // error type when catching is unknown
  static userFacingMessage = msg`Processing image error, please contact customer service if it persists`;

  constructor(public sourceErr: unknown) {
    super(`Resize error: ${sourceErr}`);
    Object.setPrototypeOf(this, MediaSelectionResizeError.prototype);
    this.userFacingDescriptor = MediaSelectionResizeError.userFacingMessage;
  }
}

export class MediaSelectionUnknownError extends MediaSelectionError {
  errorType = 'MediaSelectionUnknownError';

  // error type when catching is unknown
  static userFacingMessage = msg`Unknown error, please contact customer service if it persists`;

  constructor(public sourceErr: unknown) {
    super(`Unknown error: ${sourceErr}`);
    Object.setPrototypeOf(this, MediaSelectionUnknownError.prototype);
    this.userFacingDescriptor = MediaSelectionUnknownError.userFacingMessage;
  }
}

// Transient, recoverable failure to open the camera / photo library. On Android,
// react-native-image-picker surfaces this when startActivityForResult throws — most
// commonly a framework NullPointerException on a null window decor view (see the
// react-native-image-picker patch, which tags these with a "launchFailed:" prefix).
// Retrying almost always succeeds, so unlike MediaSelectionUnknownError this is NOT
// reported to Sentry and shows the user an actionable "try again" message.
export class MediaSelectionLaunchError extends MediaSelectionError {
  errorType = 'MediaSelectionLaunchError';

  static userFacingMessage = msg`Couldn't open the photo picker, please try again`;

  constructor(public sourceErr: unknown) {
    super(`Launch failed: ${sourceErr}`);
    Object.setPrototypeOf(this, MediaSelectionLaunchError.prototype);
    this.userFacingDescriptor = MediaSelectionLaunchError.userFacingMessage;
  }
}

// Recoverable failure to create the app-specific cache file the picked photo/video is
// copied into, specifically when the native module confirmed the cause was out of
// storage. Like MediaSelectionLaunchError this is a device-condition
// error, not a code bug, so it is NOT reported to Sentry and shows the user an actionable
// "Not enough storage" message.
export class MediaSelectionFileCreateError extends MediaSelectionError {
  errorType = 'MediaSelectionFileCreateError';

  static userFacingMessage = msg`Not enough storage on your device, free up space and try again`;

  constructor(public sourceErr: unknown) {
    super(`File create failed: ${sourceErr}`);
    Object.setPrototypeOf(this, MediaSelectionFileCreateError.prototype);
    this.userFacingDescriptor = MediaSelectionFileCreateError.userFacingMessage;
  }
}

// Transient loss of read access to a URI handed back by the Android photo picker.
// The picker grants the app a temporary read permission on the selected item; when
// that grant lapses before we read the file — most often on cloud-only Google Photos
// items, where the read is deferred behind a download — MediaProvider throws a
// SecurityException ("... does not have permission to access picker uri: ..."). The
// picker did open and the user did choose a file, so this is neither a launch failure
// nor a denied app-level media permission: re-picking the item usually succeeds. Like
// MediaSelectionLaunchError this is NOT reported to Sentry.
// See https://roverdotcom.atlassian.net/browse/DEV-155776
export class MediaSelectionPickerAccessError extends MediaSelectionError {
  errorType = 'MediaSelectionPickerAccessError';

  static userFacingMessage = msg`Couldn't read the selected file, please try again`;

  constructor(public sourceErr: unknown) {
    super(`Picker access failed: ${sourceErr}`);
    Object.setPrototypeOf(this, MediaSelectionPickerAccessError.prototype);
    this.userFacingDescriptor = MediaSelectionPickerAccessError.userFacingMessage;
  }
}
